import type { BestFareByDate } from "@jb-dotcom/api/best-fares";
import type { DayRenderProperties } from "@jetblue/kit/calendar";
import { clsx } from "clsx";
import { formatCompactPoints, formatCurrency } from "./format-price";

const formatPrice = (
  amount: number,
  usePoints: boolean,
  currencyCode?: string,
) => {
  if (usePoints) {
    return formatCompactPoints(amount);
  }

  return formatCurrency(amount, currencyCode);
};

type BestFaresDayRenderProperties = Pick<
  DayRenderProperties,
  "date" | "isActive" | "isDisabled"
>;

export type BestFaresDateCellProperties = BestFaresDayRenderProperties & {
  currencyCode?: string | undefined;
  fare?: BestFareByDate | undefined;
  isLoading: boolean;
  isLowestFare?: boolean | undefined;
  usePoints: boolean;
};
export const BestFaresDateCell = ({
  currencyCode,
  date,
  fare,
  isActive,
  isDisabled,
  isLoading,
  isLowestFare,
  usePoints,
}: BestFaresDateCellProperties) => {
  if (!date) {
    return null;
  }

  const dayClassName = clsx({
    "text-medium-gray": isDisabled,
    "text-royal-blue": !isDisabled && !isActive,
    "text-white": isActive,
  });

  const priceClassName = clsx("text-legal", {
    "font-bold": isLowestFare,
    "text-charcoal": !isDisabled && !isActive,
    "text-medium-gray": isDisabled,
    "text-white": isActive,
  });

  const showPriceSkeleton = isLoading && !fare && !isDisabled;
  const showPrice = fare && !isDisabled;

  return (
    <span className="flex flex-col items-center leading-tight">
      <span className={dayClassName}>{date.day}</span>
      {showPriceSkeleton && (
        <span className="mt-0.5 h-2 w-7 animate-pulse rounded-sm bg-light-gray" />
      )}
      {showPrice && (
        <span className={priceClassName}>
          {formatPrice(fare.amount, usePoints, currencyCode)}
        </span>
      )}
    </span>
  );
};
